import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FormCard } from "../FormCard";

const TariffAssignmentOverview = ({
    isPublic,
    connectors = [],
    customers = [],
}) => {
    const { t } = useTranslation();

    return (
        <>
            <FormCard title={t("entity.connectors")}>
                <SiteConnectorGroups connectors={connectors} />
            </FormCard>

            {!isPublic && (
                <FormCard title={t("entity.customers")}>
                    <SimpleNameList
                        items={sortedCustomers(customers)}
                        emptyText={t("tariff.preview.noCustomers")}
                    />
                </FormCard>
            )}
        </>
    );
};

function SiteConnectorGroups({ connectors = [] }) {
    const { t } = useTranslation();
    const groups = groupConnectorsBySite(connectors, t);
    const [openSites, setOpenSites] = useState(() =>
        groups.length === 1 ? { [groups[0].key]: true } : {}
    );

    if (!groups.length) {
        return <p className="text-sm text-mute-500">{t("tariff.preview.noConnectors")}</p>;
    }

    const toggleSite = (key) => {
        setOpenSites((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <div className="flex flex-col">
            {groups.map((group) => {
                const open = Boolean(openSites[group.key]);
                return (
                    <div key={group.key} className="border-b border-mute-200 last:border-b-0">
                        <button
                            type="button"
                            className="flex w-full items-center justify-between gap-4 py-4 text-left"
                            onClick={() => toggleSite(group.key)}
                            aria-expanded={open}
                        >
                            <span className="flex min-w-0 items-center gap-2">
                                <span className="truncate text-sm font-semibold">{group.label}</span>
                                <span className="shrink-0 text-sm text-mute-600">
                                    {group.connectors.length}
                                </span>
                            </span>
                            <FontAwesomeIcon
                                icon={open ? faChevronUp : faChevronDown}
                                className="shrink-0 text-sm text-mute-600"
                            />
                        </button>
                        {open && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-1 pl-6 pb-4">
                                {group.connectors.map((connector) => (
                                    <div
                                        key={connector.key}
                                        className="py-1.5 text-sm font-normal text-mute-700"
                                    >
                                        {connector.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function SimpleNameList({ items, emptyText }) {
    if (!items.length) {
        return <p className="text-sm text-mute-500">{emptyText}</p>;
    }

    return (
        <ul>
            {items.map((item) => (
                <li
                    key={item.key}
                    className="py-4 border-b border-mute-200 text-sm font-semibold last:border-b-0"
                >
                    {item.label}
                </li>
            ))}
        </ul>
    );
}

function groupConnectorsBySite(connectors, t) {
    const groups = new Map();
    [...(connectors || [])].forEach((connector) => {
        const key =
            connector.chargingSitePublicId || connector.chargingSiteName || "no-site";
        const label = connector.chargingSiteName || connector.chargingSitePublicId || t("tariff.preview.noSite");
        const name =
            connector.chargingConnectorName || connector.chargingConnectorPublicId || "";
        if (!groups.has(key)) {
            groups.set(key, { key, label, connectors: [] });
        }
        groups.get(key).connectors.push({
            key: connector.chargingConnectorPublicId,
            label: name,
        });
    });
    return [...groups.values()]
        .map((group) => ({
            ...group,
            connectors: group.connectors.sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

function sortedCustomers(customers) {
    return [...(customers || [])]
        .map((customer) => ({
            key: customer.customerPublicId,
            label: customer.customerName || customer.name || customer.customerPublicId || "",
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

export default TariffAssignmentOverview;
